import * as secp from "@noble/secp256k1";
import { keccak_256 } from "@noble/hashes/sha3";
import { bytesToHex } from "@noble/hashes/utils";

const MACHINE_URL = "/machine.json";
const API_PASSPORTS = "/api/passports";
const RPC_CATALOG_URL = "/base_rpc.json";
/** Fallback jeśli katalog nie załaduje się — pełna lista też w public/base_rpc.json */
const BASE_RPCS_FALLBACK = [
  "https://rpc.baseazul.dev",
  "https://base.rpc.blxrbdn.com",
  "https://rpc.owlracle.info/base/70d38ce1826c4a60bb2a8e05a6c8b20f",
  "https://xrpc.cl/base",
  "https://base.drpc.org",
  "https://base.public.blockpi.network/v1/rpc/public",
  "https://gateway.tenderly.co/public/base",
  "https://base.gateway.tenderly.co",
  "https://base.rpc.sentio.xyz",
  "https://base-mainnet.gateway.tatum.io",
  "https://base-rpc.publicnode.com",
  "https://base.api.pocket.network",
  "https://base-public.nodies.app",
  "https://lb.routeme.sh/rpc/evm/8453",
  "https://base-mainnet.public.blastapi.io",
  "https://developer-access-mainnet.base.org",
  "https://mainnet.base.org",
  "https://8453.rpc.thirdweb.com",
];
let BASE_RPCS = [...BASE_RPCS_FALLBACK];

async function loadRpcCatalog() {
  try {
    const data = await fetch(RPC_CATALOG_URL, { cache: "no-store" }).then((r) => r.json());
    const https = (data.https || []).filter((u) => typeof u === "string" && u.startsWith("http"));
    if (https.length) BASE_RPCS = [...new Set(https)];
  } catch {
    /* zostaw fallback */
  }
}

let lastVault = null;
let forgeAnim = null;

function setMode(mode) {
  const human = document.getElementById("human-root");
  const machine = document.getElementById("machine-root");
  const btnH = document.getElementById("btn-human");
  const btnM = document.getElementById("btn-machine");
  const isMachine = mode === "machine";
  human.hidden = isMachine;
  machine.hidden = !isMachine;
  btnH.classList.toggle("on", !isMachine);
  btnM.classList.toggle("on", isMachine);
  if (isMachine) loadMachine();
}

async function loadMachine() {
  const pre = document.getElementById("machine-pre");
  try {
    const [machine, registry] = await Promise.all([
      fetch(MACHINE_URL, { cache: "no-store" }).then((r) => r.json()),
      fetch(API_PASSPORTS, { cache: "no-store" }).then((r) => r.json()).catch(() => null),
    ]);
    pre.textContent = JSON.stringify({ ...machine, live_registry: registry }, null, 2);
  } catch (e) {
    pre.textContent = JSON.stringify({ error: String(e) }, null, 2);
  }
}

function privateKeyToAddress(privBytes) {
  const pub = secp.getPublicKey(privBytes, false);
  const hash = keccak_256(pub.slice(1));
  return "0x" + bytesToHex(hash.slice(-20));
}

function shortAddr(a) {
  if (!a || a.length < 12) return a || "—";
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

async function rpcCall(method, params) {
  let lastErr;
  for (const url of BASE_RPCS) {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message || "rpc_error");
      return data.result;
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("rpc_failed");
}

async function getBalanceEth(address) {
  const hex = await rpcCall("eth_getBalance", [address, "latest"]);
  const wei = BigInt(hex);
  const whole = wei / 10n ** 18n;
  const frac = wei % 10n ** 18n;
  const fracStr = frac.toString().padStart(18, "0").slice(0, 6);
  return `${whole}.${fracStr} ETH`;
}

function bootAshBackground() {
  const canvas = document.getElementById("ash-canvas");
  const ctx = canvas.getContext("2d");
  const particles = [];
  const resize = () => {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  };
  resize();
  window.addEventListener("resize", resize);
  for (let i = 0; i < 70; i++) {
    particles.push({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 2.2 + 0.4,
      vy: -(Math.random() * 0.45 + 0.12),
      vx: (Math.random() - 0.5) * 0.25,
      a: Math.random() * 0.5 + 0.15,
    });
  }
  const tick = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const g = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    g.addColorStop(0, "#07090c");
    g.addColorStop(0.45, "#121820");
    g.addColorStop(1, "#1a100c");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // distant ruins silhouette
    ctx.fillStyle = "rgba(0,0,0,0.45)";
    ctx.beginPath();
    ctx.moveTo(0, canvas.height * 0.72);
    for (let x = 0; x <= canvas.width; x += 40) {
      const h = Math.sin(x * 0.01) * 40 + Math.cos(x * 0.02) * 25 + 90;
      ctx.lineTo(x, canvas.height * 0.78 - h);
    }
    ctx.lineTo(canvas.width, canvas.height);
    ctx.lineTo(0, canvas.height);
    ctx.fill();

    for (const p of particles) {
      p.x += p.vx;
      p.y += p.vy;
      if (p.y < -10) {
        p.y = canvas.height + 10;
        p.x = Math.random() * canvas.width;
      }
      ctx.beginPath();
      ctx.fillStyle = `rgba(255, 140, 80, ${p.a})`;
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }
    requestAnimationFrame(tick);
  };
  tick();
}

function bootForgeCanvas() {
  const canvas = document.getElementById("forge-canvas");
  const ctx = canvas.getContext("2d");
  const state = {
    phase: "idle", // idle | stamping | done
    t: 0,
    flash: 0,
    address: "",
    handle: "",
  };
  forgeAnim = state;

  const draw = () => {
    state.t += 1;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // chamber
    const bg = ctx.createRadialGradient(w * 0.5, h * 0.55, 20, w * 0.5, h * 0.5, w * 0.55);
    bg.addColorStop(0, "#2a1812");
    bg.addColorStop(0.55, "#0c1218");
    bg.addColorStop(1, "#05070a");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    // grid
    ctx.strokeStyle = "rgba(125,255,179,0.08)";
    ctx.lineWidth = 1;
    for (let x = 40; x < w; x += 40) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    for (let y = 40; y < h; y += 40) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }

    // anvil / stamp plate
    const cx = w / 2;
    const cy = h * 0.62;
    ctx.fillStyle = "#1b242e";
    ctx.strokeStyle = "rgba(125,255,179,0.35)";
    ctx.lineWidth = 2;
    roundRect(ctx, cx - 160, cy - 50, 320, 110, 8);
    ctx.fill();
    ctx.stroke();

    // descending stamp
    let stampY = h * 0.18;
    if (state.phase === "stamping") {
      const p = Math.min(1, state.t / 45);
      stampY = h * 0.18 + (cy - 120 - h * 0.18) * easeOut(p);
      if (p >= 1) {
        state.phase = "done";
        state.flash = 1;
        state.t = 0;
      }
    } else if (state.phase === "done") {
      stampY = cy - 120;
      state.flash = Math.max(0, state.flash - 0.03);
    }

    ctx.fillStyle = "#2c333c";
    ctx.strokeStyle = state.phase === "idle" ? "rgba(255,106,61,0.45)" : "rgba(255,179,71,0.9)";
    roundRect(ctx, cx - 70, stampY, 140, 70, 6);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = "#ff6a3d";
    ctx.font = "700 14px Orbitron, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("PASS", cx, stampY + 42);

    // energy ring
    const pulse = 0.5 + Math.sin(state.t / 12) * 0.5;
    ctx.beginPath();
    ctx.strokeStyle = `rgba(125,255,179,${0.15 + pulse * 0.25})`;
    ctx.lineWidth = 2;
    ctx.arc(cx, cy, 150 + pulse * 8, 0, Math.PI * 2);
    ctx.stroke();

    if (state.flash > 0) {
      ctx.fillStyle = `rgba(255, 200, 120, ${state.flash * 0.45})`;
      ctx.fillRect(0, 0, w, h);
    }

    // hologram passport card
    if (state.phase === "done" && state.address) {
      const cardX = cx - 150;
      const cardY = cy - 40;
      ctx.fillStyle = "rgba(10, 18, 24, 0.92)";
      ctx.strokeStyle = "rgba(125,255,179,0.7)";
      roundRect(ctx, cardX, cardY, 300, 90, 10);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "#7dffb3";
      ctx.font = "12px Share Tech Mono, monospace";
      ctx.textAlign = "left";
      ctx.fillText("CAPITALCITY_PASS", cardX + 16, cardY + 24);
      ctx.fillStyle = "#ffb347";
      ctx.fillText((state.handle || "UNLABELED").toUpperCase(), cardX + 16, cardY + 46);
      ctx.fillStyle = "#c5d0d8";
      ctx.fillText(state.address, cardX + 16, cardY + 70);
    }

    // idle sparks
    if (state.phase === "idle") {
      for (let i = 0; i < 8; i++) {
        const ang = state.t / 30 + i;
        const x = cx + Math.cos(ang) * (90 + (i % 3) * 18);
        const y = cy - 20 + Math.sin(ang * 1.3) * 28;
        ctx.fillStyle = "rgba(255,140,80,0.7)";
        ctx.fillRect(x, y, 2, 2);
      }
    }

    requestAnimationFrame(draw);
  };
  draw();
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function easeOut(t) {
  return 1 - Math.pow(1 - t, 3);
}

function runStampAnimation(handle, address) {
  return new Promise((resolve) => {
    if (!forgeAnim) {
      resolve();
      return;
    }
    forgeAnim.phase = "stamping";
    forgeAnim.t = 0;
    forgeAnim.handle = handle;
    forgeAnim.address = address;
    document.getElementById("forge-hud").textContent = "STAMPING · FORGING KEY MATERIAL";
    const wait = () => {
      if (forgeAnim.phase === "done") {
        document.getElementById("forge-hud").textContent = `ISSUED · ${shortAddr(address)}`;
        resolve();
        return;
      }
      requestAnimationFrame(wait);
    };
    wait();
  });
}

async function issuePassport() {
  const handle = (document.getElementById("handle-input").value || "").trim() || null;
  document.getElementById("forge-hud").textContent = "ENTROPY · SAMPLING";
  const priv = secp.utils.randomPrivateKey();
  const address = privateKeyToAddress(priv);
  const created_utc = new Date().toISOString();
  lastVault = {
    contract: "capitalcity-pass.vault.v1",
    handle,
    address,
    private_key: "0x" + bytesToHex(priv),
    chain_id: 8453,
    chain: "base",
    preferred_rpc: BASE_RPCS[Math.floor(Math.random() * BASE_RPCS.length)] || "https://mainnet.base.org",
    rpc_catalog: "/base_rpc.json",
    created_utc,
    custody: "owner-browser-download",
    warning: "PRIVATE KEY — never commit, never paste to LLM or 1F916 posts",
  };

  await runStampAnimation(handle || "unlabeled", address);

  const box = document.getElementById("issue-result");
  box.hidden = false;
  box.textContent =
    `address: ${address}\nchain: base (8453)\nhandle label: ${handle || "—"}\n` +
    `preferred_rpc: ${lastVault.preferred_rpc}\n` +
    `created_utc: ${created_utc}\n\n` +
    `Vault ready for owner download.\n` +
    `Ledger write is AGENT-ONLY (runtime auth) — this page cannot publish.`;
  document.getElementById("btn-download").disabled = false;
  document.getElementById("hero-meta").textContent = `forged · ${shortAddr(address)} · ledger via agent`;
}

function downloadVault() {
  if (!lastVault) return;
  const name = `capitalcity-pass-${(lastVault.handle || "agent").replace(/[^\w.-]+/g, "_")}-${lastVault.address.slice(2, 10)}.json`;
  const blob = new Blob([JSON.stringify(lastVault, null, 2) + "\n"], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

async function loadLedger() {
  const body = document.getElementById("ledger-body");
  const meta = document.getElementById("ledger-meta");
  const counter = document.getElementById("counter-num");
  const counterSub = document.getElementById("counter-sub");
  body.innerHTML = `<tr><td colspan="5" class="mono">syncing ledger…</td></tr>`;
  try {
    const res = await fetch(API_PASSPORTS, { cache: "no-store" });
    const data = await res.json();
    const list = data.passports || [];
    counter.textContent = String(data.count ?? list.length);
    counterSub.textContent = "agent-verified passports";
    meta.textContent = `${list.length} IDs · human read-only · balances via Base RPC`;
    document.getElementById("hero-meta").textContent = `ledger live · ${list.length} issued`;

    if (!list.length) {
      body.innerHTML = `<tr><td colspan="5" class="mono">empty forge — agents must register via runtime</td></tr>`;
      return;
    }

    body.innerHTML = "";
    list.forEach((row, i) => {
      const tr = document.createElement("tr");
      tr.innerHTML = `
        <td>${i + 1}</td>
        <td>${escapeHtml(row.handle)}</td>
        <td class="addr" title="${escapeHtml(row.address)}">${escapeHtml(row.address)}</td>
        <td class="bal mono">…</td>
        <td>${escapeHtml((row.registered_utc || "").slice(0, 10) || "—")}</td>`;
      body.appendChild(tr);
      const balCell = tr.querySelector(".bal");
      getBalanceEth(row.address)
        .then((bal) => {
          balCell.textContent = bal;
          if (!bal.startsWith("0.000000")) balCell.classList.add("bal-hot");
        })
        .catch(() => {
          balCell.textContent = "n/a";
        });
    });
  } catch (e) {
    body.innerHTML = `<tr><td colspan="5" class="mono">ledger error: ${escapeHtml(String(e))}</td></tr>`;
    counter.textContent = "?";
  }
}

function escapeHtml(s) {
  return String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

document.getElementById("btn-human").addEventListener("click", () => setMode("human"));
document.getElementById("btn-machine").addEventListener("click", () => setMode("machine"));
document.getElementById("btn-issue").addEventListener("click", () => {
  issuePassport().catch((e) => {
    const box = document.getElementById("issue-result");
    box.hidden = false;
    box.textContent = `error: ${e}`;
    document.getElementById("forge-hud").textContent = "FAULT · RETRY";
  });
});
document.getElementById("btn-download").addEventListener("click", downloadVault);
document.getElementById("btn-refresh").addEventListener("click", () => loadLedger());

bootAshBackground();
bootForgeCanvas();
loadRpcCatalog().then(() => loadLedger());
